// Everything that leaves the device goes through here. A passphrase is
// stretched into an AES-GCM key that never leaves memory; the repo only ever
// sees `{ v, iv, ct }` envelopes. Version 2 gzips the plaintext before it is
// encrypted - ciphertext doesn't compress - so a large note is a smaller
// upload; version 1, uncompressed, is still read.
import {
  fromBase64,
  toBase64,
} from './base64';

const ITERATIONS = 600_000;
const KEYCHECK_PLAINTEXT = 'notes-keycheck';

export const randomSalt = () =>
  toBase64(crypto.getRandomValues(new Uint8Array(16)));

export const deriveKey = async (
  passphrase: string,
  salt: string,
  iterations = ITERATIONS,
): Promise<CryptoKey> => {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: fromBase64(salt), iterations },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
};

const VERSION = 2;

const through = async (
  bytes: BufferSource,
  stream: CompressionStream | DecompressionStream,
) =>
  new Uint8Array(
    await new Response(new Blob([bytes]).stream().pipeThrough(stream))
      .arrayBuffer(),
  );

export const encrypt = async (key: CryptoKey, plaintext: string) => {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    await through(
      new TextEncoder().encode(plaintext),
      new CompressionStream('gzip'),
    ),
  );
  return JSON.stringify({
    v: VERSION,
    iv: toBase64(iv),
    ct: toBase64(new Uint8Array(ct)),
  });
};

// Throws on a wrong key or a tampered envelope: GCM authenticates the
// ciphertext.
export const decrypt = async (key: CryptoKey, envelope: string) => {
  const { v, iv, ct } = JSON.parse(envelope);
  if (v !== 1 && v !== VERSION) {
    throw new Error(`unknown envelope version ${v}`);
  }
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64(iv) },
    key,
    fromBase64(ct),
  );
  return new TextDecoder().decode(
    v === 1
      ? plaintext
      : await through(plaintext, new DecompressionStream('gzip')),
  );
};

// Stored unencrypted in the repo so a second device can derive the same key
// and tell a wrong passphrase apart from a broken note. `totp` is the
// authenticator secret, encrypted with that key, once one is set up.
export interface Keycheck {
  v: 1;
  salt: string;
  iterations: number;
  check: string;
  totp?: string;
}

export const makeKeycheck = async (passphrase: string) => {
  const salt = randomSalt();
  const key = await deriveKey(passphrase, salt);
  const keycheck: Keycheck = {
    v: 1,
    salt,
    iterations: ITERATIONS,
    check: await encrypt(key, KEYCHECK_PLAINTEXT),
  };
  return { key, keycheck };
};

export const unlockKeycheck = async (
  passphrase: string,
  keycheck: Keycheck,
): Promise<CryptoKey | null> => {
  const key = await deriveKey(passphrase, keycheck.salt, keycheck.iterations);
  try {
    return (await decrypt(key, keycheck.check)) === KEYCHECK_PLAINTEXT
      ? key
      : null;
  } catch {
    return null;
  }
};
